import React, { useEffect, useRef, useState } from "react";
import { BuildingIcon, ChevronDownIcon } from "lucide-react";
import useApi from "@/hooks/useApi";
import { useCompanyStore } from "@/Store";
import { ApiResponse } from "@/types/IApi";
import { Company, toCompanyOption } from "@/types/ICompany";
import S from "./styles.module.scss";

const CompanySwitcher = ({
  variant = "default",
}: {
  variant?: "default" | "sidebar";
}) => {
  const { getRequest } = useApi();
  const { companies, setCompanies, currentCompanyId, setCurrentCompanyId } =
    useCompanyStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await getRequest("/api/company/mine");
        const data = (await res.json()) as ApiResponse<Company[]>;
        const list = (data.response ?? []).map(toCompanyOption);
        setCompanies(list);

        const isValid = list.some((company) => company.id === currentCompanyId);
        if (list.length && (!currentCompanyId || !isValid)) {
          setCurrentCompanyId(list[0].id);
        }
      } catch {
        // silencioso: backend usa a primeira empresa como fallback
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = companies.find((c) => c.id === currentCompanyId);

  const handleSelect = (id: number) => {
    setOpen(false);
    if (id !== currentCompanyId) {
      // A troca remonta o conteúdo roteado (Layout usa currentCompanyId como
      // key), refazendo os fetches com a nova empresa sem recarregar a página.
      setCurrentCompanyId(id);
    }
  };

  if (!companies.length) return null;

  return (
    <div
      className={`${S.switcher} ${variant === "sidebar" ? S.sidebar : ""}`}
      ref={ref}
    >
      <button
        type="button"
        className={S.trigger}
        onClick={() => setOpen((o) => !o)}
      >
        <BuildingIcon size={15} className={S.icon} />
        <span className={S.name}>{current?.name ?? "Empresa"}</span>
        <ChevronDownIcon size={11} className={S.chevron} />
      </button>

      {open && (
        <ul className={S.menu}>
          <span className={S.menuTitle}>Empresa atual</span>
          {companies.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className={`${S.item} ${c.id === currentCompanyId ? S.active : ""}`}
                onClick={() => handleSelect(c.id)}
              >
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CompanySwitcher;
